/* Homepage scroll reveal — Aborder Products.
   Mirrors assets/product-media-reveal.js.

   Safety contract (unchanged from the previous revision — do not weaken):
   1. The CSS hidden state is gated on `.hp-reveal-ready` on <body>, which ONLY
      this file adds. If this script never runs, never parses, or bails, nothing
      on the homepage is ever hidden.
   2. Nothing inside a `.swiper` is ever observed. Swiper slides sit off-screen
      horizontally, so IntersectionObserver never fires for slides 2..n and they
      would stay at opacity 0 forever. Same trap already avoided on the PDP.
      Carousels still animate: the TRACK is observed instead of its slides, and
      the per-slide cascade is nth-child delays in CSS. See TRACKS below.
   3. A 2.5s failsafe reveals anything on screen that the observer missed, and
      a throttled scroll listener does the same as the reader moves down. The
      sweep is viewport-scoped on purpose: revealing the whole page on a timer
      is what previously stopped everything below the fold from animating.
   4. Separately strips the inline `style="opacity: 0;"` that
      sections/image-banner.liquid:143-146 stamps on `.animated-heading--inner`,
      in case animateHeading() in assets/global.js never runs. That guard is NOT
      part of the reveal system and fires even under reduced motion.
   5. Only runs on the homepage; the whole init is wrapped in try/catch that
      fails open.

   What changed in this revision: motion is no longer one uniform fade-up.
   Each element type gets a variant chosen to suit its shape — headings wipe up
   behind a mask, imagery scales in, split panels slide from the side. The
   variant is written to `data-hp-variant` and every variant is implemented in
   section-homepage-polish.css. An unknown or missing variant falls back to the
   plain fade-up, so a typo degrades rather than breaks. */
(function () {
  'use strict';

  var FAILSAFE_MS = 2500;
  var HEADING_FAILSAFE_MS = 2500;

  /* Section-level blocks only. Never a `.swiper-slide`, never a `.card-wrapper`
     inside a carousel — those are filtered out below as well, belt and braces.
     The ticker is deliberately absent: it is a marquee that already moves, and
     its blocks are duplicated into a second line, so revealing them fights the
     animation that is already there. Carousel product cards are absent for the
     swiper reason above — their `.section-header` still animates, so the
     section is not static.

     Order matters: the first entry whose selector matches an element wins, so
     put narrower selectors above broader ones.

     `.newsletter-form__inner` and `.video-banner__texts` are retained even
     though both sections were removed from the homepage — they cost nothing
     when absent and keep working if either section is added back in the
     theme editor. */
  var REVEAL_MAP = [
    { sel: '.section-header', variant: 'mask' },
    { sel: '.stat-band__intro', variant: 'mask' },
    { sel: '.rich-text__wrapper', variant: 'mask' },

    { sel: '.stat-band__item', variant: 'up' },
    { sel: '.shop-by-space__col', variant: 'up' },
    { sel: '.multicolumn-card', variant: 'up' },
    { sel: '.brands__item-wrapper', variant: 'up' },
    { sel: '.collapsible-content .product-popup', variant: 'up' },
    { sel: '.newsletter-form__inner', variant: 'up' },
    { sel: '.video-banner__texts', variant: 'up' },
    { sel: '.image-banner__content-wrapper', variant: 'up' },

    { sel: '.final-cta__copy', variant: 'mask' },

    { sel: '.banner-grid__card', variant: 'scale' },
    { sel: '.categories-list__item', variant: 'scale' },
    { sel: '.card-grid__card', variant: 'scale' },
    { sel: '.image-collage__card', variant: 'scale' },
    /* The mosaic tiles carry a resting translateY offset of their own for the
       staggered composition, so they are deliberately NOT given a transform
       variant here — the shared fade would fight it. Opacity only. */
    { sel: '.final-cta__tile', variant: 'fade' },

    { sel: '.image-with-text__card', variant: 'side' },

    /* The decoration panel beside the feature image. It needs the reveal class
       so its own children can key their draw-in off `.hp-reveal--in`, but the
       panel itself must not move or fade as a unit — the pieces animate
       individually. `fade` leaves the transform alone; the opacity it does
       apply lands on a container whose children are already hidden, so it is
       not visible as a second fade. */
    { sel: '.iwt-decor', variant: 'fade' },

    /* The copy beside it, so the words and the frame arrive together rather
       than the geometry drawing itself around static text. */
    { sel: '.image-with-text__heading', variant: 'mask' }
  ];

  var CANDIDATES = REVEAL_MAP.map(function (entry) {
    return entry.sel;
  }).join(',');

  /* Carousel tracks.

     The slides inside these cannot be observed individually — see the note at
     the top of this file — so the TRACK is observed instead and the cascade is
     done with nth-child delays in CSS. One target, one class, and a slide that
     is parked off-screen to the right can no longer be stranded invisible.

     These are the wrappers, not the `.swiper` roots: the wrapper's children
     are exactly the slides, which is what `.hp-track > *` in
     section-homepage-polish.css styles. */
  var TRACKS = '.categories-list__wrapper, .popular-products__wrapper';

  function variantFor(el) {
    for (var i = 0; i < REVEAL_MAP.length; i++) {
      try {
        if (el.matches(REVEAL_MAP[i].sel)) return REVEAL_MAP[i].variant;
      } catch (e) {
        /* An unsupported selector must not take the whole loop down. */
      }
    }
    return 'up';
  }

  function isHome() {
    return document.body && document.body.classList.contains('template-index');
  }

  function revealAll() {
    var hidden = document.querySelectorAll('.hp-reveal:not(.hp-reveal--in)');
    for (var i = 0; i < hidden.length; i++) {
      hidden[i].classList.add('hp-reveal--in');
    }
    var tracks = document.querySelectorAll('.hp-track:not(.hp-track--in)');
    for (var t = 0; t < tracks.length; t++) {
      tracks[t].classList.add('hp-track--in');
    }
  }

  /* Reveal only what is actually on screen.

     The previous failsafe called revealAll() on a timer, which defeated the
     whole feature: 2.5s after load every section on the page was already
     revealed, so nothing below the first screen ever animated. A sweep scoped
     to the viewport keeps the safety property (nothing on screen can stay
     invisible) without spending the animation before the reader arrives. */
  function sweepVisible() {
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!vh) {
      revealAll();
      return;
    } /* no measurable viewport: fail open */

    var hidden = document.querySelectorAll('.hp-reveal:not(.hp-reveal--in)');
    for (var i = 0; i < hidden.length; i++) {
      var r = hidden[i].getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) hidden[i].classList.add('hp-reveal--in');
    }

    var tracks = document.querySelectorAll('.hp-track:not(.hp-track--in)');
    for (var t = 0; t < tracks.length; t++) {
      var tr = tracks[t].getBoundingClientRect();
      if (tr.top < vh && tr.bottom > 0) tracks[t].classList.add('hp-track--in');
    }
  }

  /* Scroll backstop: if IntersectionObserver is present but never delivers,
     this still reveals each section as the reader reaches it. Throttled by
     timestamp rather than rAF, which browsers suspend on hidden tabs. */
  function attachScrollBackstop() {
    var last = 0;
    function onScroll() {
      var now = Date.now();
      if (now - last < 120) return;
      last = now;
      sweepVisible();
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  /* Decorative drift for the stat band's background rings.

     Writes a single custom property on the section; the CSS decides what to do
     with it. Nothing here reads or writes layout-affecting properties, and the
     rings are pseudo-elements with `pointer-events: none`, so the worst case if
     this misbehaves is that two circles sit still. */
  function attachStatDrift() {
    var band = document.querySelector('.stat-band');
    if (!band || !window.requestAnimationFrame) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    var queued = false;

    function update() {
      queued = false;
      var vh = window.innerHeight || 0;
      if (!vh) return;
      var rect = band.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) return;
      /* -1 when the band is entering from below, +1 as it leaves the top. */
      var progress = 1 - (rect.top + rect.height / 2) / (vh / 2 + rect.height / 2);
      if (progress < -1) progress = -1;
      if (progress > 1) progress = 1;
      band.style.setProperty('--sb-drift', (progress * 26).toFixed(2) + 'px');
    }

    function onScroll() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* Fail-open for base.css:1836 `.animated-heading--inner { opacity: 0 }` plus
     the inline attribute. Runs unconditionally. */
  function guardAnimatedHeadings() {
    setTimeout(function () {
      var nodes = document.querySelectorAll('.animated-heading--inner');
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        var computed = window.getComputedStyle(el).opacity;
        if (parseFloat(computed) > 0.01) continue;
        el.style.removeProperty('opacity');
        el.classList.add('hp-heading-forced');
      }
    }, HEADING_FAILSAFE_MS);
  }

  function initReveal() {
    if (!isHome()) return;

    if (
      !('IntersectionObserver' in window) ||
      !Element.prototype.closest ||
      !Element.prototype.matches ||
      (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) {
      return; /* `.hp-reveal-ready` never added => CSS hidden state never applies. */
    }

    var all = document.querySelectorAll(CANDIDATES);
    var items = [];

    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.classList.contains('hp-reveal')) continue; /* already handled */
      if (el.classList.contains('swiper-slide')) continue; /* carousel slide */
      if (el.closest('.swiper')) continue; /* inside a carousel */
      if (el.closest('.swiper-wrapper')) continue;
      if (el.closest('.swiper-container')) continue;
      items.push(el);
    }

    var tracks = [];
    var trackEls = document.querySelectorAll(TRACKS);
    for (var t = 0; t < trackEls.length; t++) {
      if (!trackEls[t].classList.contains('hp-track')) tracks.push(trackEls[t]);
    }

    if (!items.length && !tracks.length) return;

    document.body.classList.add('hp-reveal-ready');

    var observer = new IntersectionObserver(
      function (entries, obs) {
        for (var j = 0; j < entries.length; j++) {
          if (!entries[j].isIntersecting) continue;
          var target = entries[j].target;
          /* One observer serves both kinds of target; the element says which
             class it is waiting for, so a track can never be handed the
             element class or the other way round. */
          target.classList.add(
            target.classList.contains('hp-track') ? 'hp-track--in' : 'hp-reveal--in'
          );
          obs.unobserve(target);
        }
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
    );

    /* Reset per-section counters first: the theme editor re-runs init on
       `shopify:section:load`, and a stale counter would push the stagger out to
       absurd delays on every reload. */
    var hosts = document.querySelectorAll('.shopify-section');
    for (var h = 0; h < hosts.length; h++) hosts[h].__hpRevealSeen = 0;

    for (var k = 0; k < items.length; k++) {
      /* Stagger restarts per section so each block cascades from its own first
         item, instead of inheriting a count from whatever came before it. */
      var host = items[k].closest('.shopify-section') || document.body;
      var seen = host.__hpRevealSeen || 0;
      host.__hpRevealSeen = seen + 1;

      items[k].classList.add('hp-reveal');
      items[k].setAttribute('data-hp-variant', variantFor(items[k]));

      /* Alternating side entry for split panels, so a left/right pair converges
         rather than both sliding the same way. */
      if (items[k].getAttribute('data-hp-variant') === 'side') {
        items[k].setAttribute('data-hp-side', seen % 2 === 0 ? 'left' : 'right');
      }

      items[k].style.setProperty('--hp-reveal-delay', Math.min(seen, 5) * 80 + 'ms');
      observer.observe(items[k]);

      /* Anything already in view at load reveals on the next frame rather than
         waiting for a scroll — avoids an above-the-fold flash of nothing. */
      var rect = items[k].getBoundingClientRect();
      if (rect.top < (window.innerHeight || 0)) {
        (function (el) {
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              el.classList.add('hp-reveal--in');
            });
          });
        })(items[k]);
      }
    }

    for (var m = 0; m < tracks.length; m++) {
      tracks[m].classList.add('hp-track');
      observer.observe(tracks[m]);

      var trect = tracks[m].getBoundingClientRect();
      if (trect.top < (window.innerHeight || 0)) {
        (function (el) {
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              el.classList.add('hp-track--in');
            });
          });
        })(tracks[m]);
      }
    }

    /* Catches an observer that never fires for content already on screen,
       then hands off to the scroll backstop for everything below it. */
    setTimeout(sweepVisible, FAILSAFE_MS);
    attachScrollBackstop();
  }

  function init() {
    try {
      initReveal();
    } catch (e) {
      /* Any failure must leave the page fully visible. */
      if (document.body) document.body.classList.remove('hp-reveal-ready');
      revealAll();
    }
    try {
      attachStatDrift();
    } catch (e) {
      /* Purely decorative; a failure here must not reach the reveal system. */
    }
    guardAnimatedHeadings();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders a section without a full page load. */
  document.addEventListener('shopify:section:load', init);
})();
